import { createContext, useContext, useState, useMemo } from "react";
import type { ReactNode, Dispatch, SetStateAction } from "react";
import { useUserInfo } from "../hooks/useUserInfo";
import { useReferenceLists } from "./GlobalGetLists";
import { useCurrentUser } from "./CurrentUserContext";
import { buildGlobalUser } from "@/utils/resolveGlobalUser";

interface GlobalContextType {
  rightSideBarOpen: boolean;
  setRightSideBarOpen: Dispatch<SetStateAction<boolean>>;
  menuOpen: boolean;
  setMenuOpen: Dispatch<SetStateAction<boolean>>;
  user: any;
  userLoading: boolean;
}

const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

export const GlobalProvider = ({ children }: { children: ReactNode }) => {
  const [rightSideBarOpen, setRightSideBarOpen] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const { account, profile, profileError } = useUserInfo();
  const { user: currentUser, loading: currentUserLoading } = useCurrentUser();
  const { users, loading: listsLoading } = useReferenceLists();

  // MSAL sessions auto-load the Graph profile once (displayName); it ends with
  // either a profile or an error, so this never stays true.
  const graphProfilePending = Boolean(account) && !profile && !profileError;

  // The internal id comes from GET /users/me (CurrentUserProvider), which works
  // for MSAL and local password login. Matching the Graph profile against the
  // users list is only a fallback when /users/me failed.
  const { user, userLoading } = useMemo(() => {
    const waitingForUsersList = !currentUser && Boolean(profile) && listsLoading;
    if (currentUserLoading || graphProfilePending || waitingForUsersList) {
      return { user: null, userLoading: true };
    }
    return {
      user: buildGlobalUser({ currentUser, profile, users }),
      userLoading: false,
    };
  }, [
    currentUser,
    currentUserLoading,
    graphProfilePending,
    listsLoading,
    profile,
    users,
  ]);

  return (
    <GlobalContext.Provider
      value={{
        rightSideBarOpen,
        setRightSideBarOpen,
        menuOpen,
        setMenuOpen,
        user,
        userLoading,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalProvider = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error("useGlobalProvider must be used within a GlobalProvider");
  }
  return context;
};
